<template>
  <layout width="800">
    <template slot="header">Import from CSV</template>
    <validation-observer ref="form">
      <v-form :disabled="importLoading">
        <v-container>
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">
              <validation-provider
                v-slot="{ errors }"
                name="Schema"
                rules="required"
              >
                <v-autocomplete
                  clearable
                  v-model="currentSchema"
                  return-object
                  :disabled="isLoadingSchemas || importLoading"
                  :loading="isLoadingSchemas"
                  data-test-schema="deviceSchema"
                  :items="schemas"
                  item-text="name"
                  item-value="id"
                  label="Schema name *"
                  class="ma-0"
                  :error-messages="errors"
                >
                  <template v-slot:item="item">
                    <div :data-test-schema="item.item.name">
                      {{ item.item.name }}
                    </div>
                  </template>
                </v-autocomplete>
              </validation-provider>
            </v-col>
            <v-col cols="6" class="pl-2">
              <validation-provider
                v-slot="{ errors }"
                name="File"
                rules="required"
              >
                <v-file-input
                  truncate-length="40"
                  @change="handleFileUpload"
                  v-model="csvFile"
                  :show-size="true"
                  accept=".csv"
                  label="File *"
                  :error-messages="errors"
                ></v-file-input>
              </validation-provider>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">
              <v-select
                hide-details
                v-model="nameFrom"
                :menu-props="{ bottom: true, offsetY: true }"
                autocomplete="false"
                :items="NAME_FROM"
                item-text="name"
                item-value="id"
                label="Objects name from *"
                class="ma-0"
              ></v-select>
            </v-col>
            <v-col cols="6" class="pl-2">
              <validation-provider
                v-if="nameFrom === 'fromSchema'"
                v-slot="{ errors }"
                name="Start numeration from"
                rules="required"
              >
                <v-text-field
                  type="number"
                  v-model.number="startNumerationFrom"
                  min="0"
                  :error-messages="errors"
                  label="Start numeration from *"
                  class="ma-0"
                ></v-text-field>
              </validation-provider>
              <validation-provider
                v-slot="{ errors }"
                name="Column"
                rules="required"
                v-else
              >
                <v-select
                  :menu-props="{ bottom: true, offsetY: true }"
                  autocomplete="false"
                  :items="headers"
                  v-model="whichColumnIsName"
                  :error-messages="errors"
                  label="Column *"
                  class="ma-0"
                ></v-select>
              </validation-provider>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <template v-if="csvFile">
        <subheader class="mx-4">Columns mapping</subheader>
        <div class="mx-4 body-2 mb-5">
          <template v-if="columns.length <= 5">
            Preview of the {{ columns.length }} objects is displayed.
          </template>
          <template v-else>
            Preview of the first 5 out of {{ columns.length }} objects is
            displayed.
          </template>
        </div>
        <v-simple-table class="bordered-table mx-4 mb-5" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th
                  class="text-left border-1"
                  v-for="(item, index) in headers"
                  :key="index"
                >
                  {{ item }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td v-for="(select, key) in columnsMap" :key="select">
                  <validation-provider
                    v-slot="{ errors }"
                    :name="columnsMap[key]"
                    rules="required"
                  >
                    <v-autocomplete
                      clearable
                      :class="{ 'red-input': columnsMap[key] === 'skip' }"
                      hide-details
                      v-model="columnsMap[key]"
                      data-test-schema="deviceSchema"
                      :items="schemaProperties"
                      item-text="name"
                      :error-messages="errors"
                      item-value="id"
                      placeholder="Property name *"
                      class="ma-0 pa-0 text-body-2"
                    >
                      <template v-slot:item="item">
                        <div
                          :data-test-schema="item"
                          :style="{
                            color: item.item.id === 'skip' ? 'red' : 'inherit'
                          }"
                        >
                          {{ item.item.name }}
                        </div>
                      </template>
                    </v-autocomplete>
                  </validation-provider>
                </td>
              </tr>
              <tr v-for="item in columns.slice(0, 5)" :key="item.name">
                <td v-for="nestedItem in item" :key="nestedItem">
                  {{ nestedItem }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div
          class="mx-4 mb-4"
          v-if="importLoading || Math.ceil(percentage) === 100"
        >
          <subheader>Progress</subheader>

          <v-progress-linear
            color="primary"
            v-model="percentage"
            height="25"
            class="mb-4"
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
          <log-editor @init="logEditor = $event" :code="logHumanReadable" />
        </div>
      </template>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text data-test-action="importFromCsvClose" @click="close">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="importFromCsvStart"
        text
        :disabled="importLoading"
        @click="importDevices"
      >
        import
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Papa from 'papaparse';
import Layout from '@/layouts/LayoutPopup';
import {
  setInteractionMode,
  ValidationObserver,
  ValidationProvider
} from 'vee-validate';
import { computed, onMounted, ref } from '@vue/composition-api';
import { useLazyQuery, useMutation } from '@vue/apollo-composable';
import GetSchemasWithProperties from '@/shared/api/GetSchemaWithProperties.graphql';
import useAuth from '@/api/auth';
import CreateObject from '@/shared/api/CreateObject.graphql';
import sortBy from 'lodash.sortby';
import LogEditor from '@/components/LogEditor/index.vue';
import getLineSeparator from '@/utils/getLineSeparator';

setInteractionMode('eager');

const NAME_FROM = [
  {
    id: 'fromSchema',
    name: 'Schema'
  },
  {
    id: 'fromColumn',
    name: 'Column'
  }
];
export default {
  name: 'ImportDevices',
  props: {},
  components: {
    LogEditor,
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit }) {
    const { user } = new useAuth();
    const percentage = ref(0);
    const csvFile = ref(null);
    const logEditor = ref(null);
    const importLoading = ref(false);
    const nameFrom = ref('fromSchema');
    const startNumerationFrom = ref(1);
    const whichColumnIsName = ref(null);
    const currentSchema = ref(null);
    const devicesCount = ref(0);
    const headers = ref([]);
    const columns = ref([]);
    const logs = ref([]);
    const logsForEditor = ref([]);
    const columnsMap = ref({});
    const form = ref(null);

    const { mutate: createObject } = useMutation(CreateObject);

    const loadSchemas = useLazyQuery(GetSchemasWithProperties, {
      filter: {
        type: {
          equalTo: 'DEVICE'
        },
        enabled: {
          equalTo: true
        }
      }
    });

    const schemas = computed(() => {
      return loadSchemas.result?.value?.schemata ?? [];
    });

    const schemaProperties = computed(() => {
      const schemasList =
        schemas.value
          .find(item => item.id === currentSchema?.value?.id)
          ?.schemaProperties.map(item => ({
            id: item.key,
            name: `${item.groupName}/${item.description || item.property}`
          })) ?? [];

      return [
        {
          name: 'Do not import',
          id: 'skip'
        },
        ...sortBy([...schemasList], 'name')
      ];
    });

    const handleFileUpload = file => {
      Papa.parse(file, {
        encoding: 'UTF-8',
        preview: 0,
        fastMode: true,
        complete: results => {
          headers.value = results.data[0];

          devicesCount.value = results.data.length - 1;
          columns.value = results.data
            .slice(1)
            .filter(subArray =>
              subArray.some(value => value && value.trim() !== '')
            )
            .map(row => {
              const obj = {};

              headers.value.forEach((header, index) => {
                obj[header.trim()] = row[index];
                columnsMap.value[header.trim()] = null;
              });

              return obj;
            });
        }
      });
    };

    const logHumanReadable = computed(() => {
      return logs.value.map(item => item).join(getLineSeparator());
    });

    const importDevice = async (item, index) => {
      const firstObject = columnsMap.value;

      const getName = () => {
        if (nameFrom.value === 'fromColumn') {
          return item[whichColumnIsName.value.trim()];
        }

        return `${currentSchema.value.name} #${index}`;
      };

      const keyedProperties = Object.entries(item)
        .filter(
          ([key]) => firstObject[key] !== null && firstObject[key] !== 'skip'
        )
        .map(([key, value]) => ({
          propertyKey: firstObject[key],
          value: value
        }));

      const objectPayload = {
        schemaId: currentSchema.value.id,
        name: getName(),
        enabled: true,
        description: null,
        editorGroup: user.value.defaultEditorgroup,
        keyedProperties
      };

      try {
        const { data } = await createObject({ input: objectPayload });
        logs.value.push(
          `${objectPayload.name} (${data.createObjectWithProperties.uuid}) successfully imported`
        );
      } catch (e) {
        logs.value.push(`${objectPayload.name} failed to import`);
        logsForEditor.value.push({
          row: index - 1,
          column: 0,
          text: `${e}`,
          type: 'error'
        });
      } finally {
        setTimeout(() => {
          logEditor?.value?.getSession()?.setAnnotations(logsForEditor.value);
        }, 0);
      }
    };

    const importDevices = async () => {
      form.value.validate().then(async success => {
        if (!success) {
          return;
        }

        importLoading.value = true;
        let index = 0;
        let count = Number(startNumerationFrom.value);

        for (const item of columns.value) {
          index += 1;
          await importDevice(item, count);
          count += 1;
          percentage.value = (index / columns.value.length) * 100;
        }

        importLoading.value = false;
      });
    };

    const close = () => {
      emit('close');
    };

    onMounted(() => {
      loadSchemas.load();
    });

    return {
      headers,
      columns,
      schemas,
      csvFile,
      handleFileUpload,
      schemaProperties,
      currentSchema,
      columnsMap,
      importDevices,
      isLoadingSchemas: loadSchemas.loading,
      nameFrom,
      NAME_FROM,
      startNumerationFrom,
      whichColumnIsName,
      percentage,
      importLoading,
      logHumanReadable,
      logEditor,
      close,
      form
    };
  }
};
</script>

<style lang="scss" scoped>
.v-data-table {
  border-radius: 0 !important;
}

.v-data-table > .v-data-table__wrapper > table {
  border-radius: 0 !important;
}

.bordered-table th:first-child,
.bordered-table td:first-child {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.bordered-table th:last-child,
.bordered-table td:last-child {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.bordered-table {
  border-radius: 0 !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.table-wrapper {
  overflow-x: auto;
  white-space: nowrap;
}

.bordered-table th {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  min-width: 230px;
  width: 230px;
  background: #f2f2f2;
  color: #000;
  padding: 8px !important;
}

.bordered-table td {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  min-width: 230px;
  width: 230px;
  white-space: normal;
  padding: 8px !important;
  word-wrap: break-word;
}

.bordered-table table {
  border-spacing: 0;
  border-collapse: collapse;
  table-layout: fixed;
}
</style>
